// src/components/PriorityStar.jsx
// ★ priority numbers (3.22.3): anyone at a client (with "Mark numbers as
// priority", on for every role by default) can star the number of a fax
// they see in Outbox or Logs → Failed Sent. Their client's failed faxes to
// that number are then always retried (5 attempts), even when the number
// keeps failing (3.23.0: 2 retries instead of 1). A number on the Super Admins' list (Console → Fax
// Retries) shows a filled star for everyone and can't be unstarred here.
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from './Notifications';
import { API_BASE } from '../firebase/config';

const last10 = n => String(n || '').replace(/\D/g, '').slice(-10);

// { global: Set, clients: { id: Set } } and a reload
export function usePriorityNumbers() {
  const { getToken, accountVersion } = useAuth();
  const [map, setMap] = useState({ global: new Set(), clients: {} });
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/portal/priority-numbers`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      setMap({ global: new Set((j.global || []).map(last10)), clients: Object.fromEntries(Object.entries(j.clients || {}).map(([id, l]) => [id, new Set(l.map(p => last10(p.number)))])) });
    } catch { /* optional */ }
  }, [getToken]);
  useEffect(() => { load(); }, [load, accountVersion]);
  return [map, load];
}

export default function PriorityStar({ fax, map, onChanged }) {
  const { getToken, shows } = useAuth();   // 3.62.16: shows = hidden in read-only View as
  const { notify } = useNotify();
  const [busy, setBusy] = useState(false);
  const n = last10(fax.toNumber);
  if (!n || !fax.accountId) return null;
  const global = map.global.has(n);
  const on = global || !!map.clients[fax.accountId]?.has(n);
  const canMark = shows('fax.priority') && !global;
  if (!on && !canMark) return null;
  async function toggle(e) {
    e.stopPropagation();
    if (!canMark || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/portal/priority-numbers`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(fax.faxId ? { faxId: fax.faxId, on: !on } : { accountId: fax.accountId, number: fax.toNumber, on: !on }) });   // by number from Fax Contacts (3.25.0)
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Could not save');
      notify(on
        ? { tone: 'info', title: 'No longer a priority number', detail: 'Failed faxes to it follow the normal retry rules again.' }
        : { tone: 'success', title: 'Priority number ★', detail: `Failed faxes from ${fax.accountName || 'your company'} to this number get 2 retries instead of 1 (in your company’s office hours), even if it keeps failing.` });
      onChanged?.();
    } catch (x) { notify({ tone: 'error', title: "Couldn't change it", detail: x.message }); }
    setBusy(false);
  }
  const title = global ? 'Priority number (set by Fax Portal support for every client)'
    : on ? 'Priority number: failed faxes to it are always retried. Click to remove.'
    : 'Mark as priority: failed faxes to this number will always be retried';
  return (
    <button onClick={toggle} disabled={!canMark || busy} title={title} aria-label={title} aria-pressed={on}
      style={{ background: 'none', border: 'none', padding: '0 4px', marginLeft: 2, cursor: canMark ? 'pointer' : 'default', fontSize: 15, lineHeight: 1, color: on ? 'var(--t-f9a825)' : 'var(--t-b0bec5)', verticalAlign: 'middle' }}>
      {on ? '★' : '☆'}
    </button>
  );
}
